if (!customElements.get('dual-product-selector')) {
  customElements.define(
    'dual-product-selector',
    class DualProductSelector extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.sectionId = this.dataset.sectionId;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.products = this.readProducts();
        this.modelContentCache = new Map();
        this.modelContentRequest = 0;
        this.modelInputs = Array.from(this.querySelectorAll('[data-model-key].dual-product-selector__model-input'));
        this.variantInputs = Array.from(this.querySelectorAll('.dual-product-selector__variant-input'));

        this.modelInputs.forEach((input) => {
          input.addEventListener('change', () => this.selectModel(input.dataset.modelKey, true));

          const card = input.nextElementSibling;
          card?.addEventListener('click', () => {
            if (input.checked) this.selectModel(input.dataset.modelKey, true);
          });
        });
        this.variantInputs.forEach((input) => {
          input.addEventListener('change', () => {
            if (input.checked) this.applySelection(input.dataset.modelKey, Number(input.value), true);
          });
        });

        // 默认自动选中一个型号，而不是停留在“等待用户选择”的空状态。
        // 优先级：liquid 里已经 checked 的 radio > data-default-model-key > 第一个可用型号。
        const checkedModelInput = this.modelInputs.find((input) => input.checked);
        const defaultModelKey = checkedModelInput?.dataset.modelKey
          || this.dataset.defaultModelKey
          || this.modelInputs[0]?.dataset.modelKey;

        if (defaultModelKey && this.products[defaultModelKey]) {
          this.selectModel(defaultModelKey, false);
        } else {
          this.showInitialModelPrice();
          this.setAwaitingModel(true);
        }
      }

      showInitialModelPrice() {
        const data = this.products['1'];
        const variant = this.getInitialVariant(data);
        if (!data?.product || !variant) return;

        this.updateMainPrice(variant);
        this.updateInstallmentMessaging(variant);

        const detail = {
          sectionId: this.sectionId,
          product: data.product,
          variant,
        };
        window.__dualProductInitialPrice = detail;
        document.dispatchEvent(new CustomEvent('dual-product:initial-price', { detail }));
      }

      setAwaitingModel(awaiting) {
        this.dataset.awaitingModel = String(awaiting);
        document.documentElement.classList.toggle('dual-product-awaiting-selection', awaiting);

        const stock = this.querySelector('.dual-product-selector__stock');
        stock?.classList.toggle('hidden', awaiting);

        const formInputs = document.querySelectorAll(
          `#product-form-${CSS.escape(this.sectionId)} [name="id"], ` +
          `#product-form-installment-${CSS.escape(this.sectionId)} [name="id"], ` +
          '.sticky-atc-bar__form [name="id"]'
        );
        formInputs.forEach((input) => {
          input.disabled = awaiting;
        });

        document
          .querySelectorAll('.product__block--buy_buttons [name="add"], .sticky-atc-bar__form [name="add"]')
          .forEach((button) => {
            if (awaiting) {
              button.disabled = true;
              button.setAttribute('aria-disabled', 'true');
            } else {
              button.removeAttribute('aria-disabled');
            }
          });

        document
          .querySelectorAll(
            '.product__block--buy_buttons .shopify-payment-button, ' +
            '.product__block--buy_buttons custom-checkout-button, ' +
            '.sticky-atc-bar .product-form__button-dynamic, ' +
            '.sticky-atc-bar custom-checkout-button'
          )
          .forEach((control) => {
            control.inert = awaiting;
            if (awaiting) {
              control.setAttribute('aria-disabled', 'true');
            } else {
              control.removeAttribute('aria-disabled');
            }
          });
      }

      readProducts() {
        return Array.from(this.querySelectorAll('[data-dual-product]')).reduce((products, script) => {
          try {
            products[script.dataset.dualProduct] = JSON.parse(script.textContent);
          } catch (error) {
            console.error('Unable to read Dual product data', error);
          }
          return products;
        }, {});
      }

      formatMoney(cents) {
        if (window.FoxTheme?.Currency?.formatMoney) {
          return window.FoxTheme.Currency.formatMoney(cents, window.FoxTheme.settings?.moneyFormat || this.moneyFormat);
        }

        const amount = (Number(cents || 0) / 100).toFixed(2);
        return this.moneyFormat
          .replace(/\{\{\s*amount\s*\}\}/g, amount.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(cents || 0) / 100)))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
      }

      selectModel(modelKey, announce) {
        const data = this.products[modelKey];
        if (!data?.product) return;

        this.modelInputs.forEach((input) => {
          input.checked = input.dataset.modelKey === String(modelKey);
        });
        this.querySelectorAll('[data-variant-panel]').forEach((panel) => {
          panel.classList.toggle('hidden', panel.dataset.variantPanel !== String(modelKey));
        });

        const checkedVariant = this.variantInputs.find(
          (input) => input.dataset.modelKey === String(modelKey) && input.checked && !input.disabled
        );
        const initialVariant = this.getInitialVariant(data, checkedVariant?.value);

        if (initialVariant) {
          const input = this.variantInputs.find((variantInput) => Number(variantInput.value) === Number(initialVariant.id));
          if (input) input.checked = true;
          this.applySelection(modelKey, initialVariant.id, announce);
        }
      }

      findVariant(product, variantId) {
        return product?.variants?.find((variant) => Number(variant.id) === Number(variantId));
      }

      getInitialVariant(data, preferredVariantId) {
        if (!data?.product) return null;

        const initialVariantId = Number(preferredVariantId || data.initialVariantId);
        return this.findVariant(data.product, initialVariantId) ||
          data.product.variants.find((variant) => variant.available) ||
          data.product.variants[0];
      }

      applySelection(modelKey, variantId, announce) {
        const data = this.products[modelKey];
        const variant = this.findVariant(data?.product, variantId);
        if (!data?.product || !variant) return;

        this.setAwaitingModel(false);
        this.currentModelKey = String(modelKey);
        this.currentProduct = data.product;
        this.currentVariant = variant;

        this.updateModelCard(modelKey, variant);
        this.updateStock(data, variant);
        this.updateForms(variant);
        this.updateMainPrice(variant);
        this.updateInstallmentMessaging(variant);
        this.updateProductCopy(data, announce);
        this.updateCoupon(data);
        this.updateGalleryImage(data, variant);
        this.updateModelContent(data);
        this.updateBrowserUrl(data, variant);
        this.updateBreadcrumbs(data);

        const detail = {
          sectionId: this.sectionId,
          modelKey: String(modelKey),
          product: data.product,
          variant,
          displayTitle: data.displayTitle || data.product.title,
          displayImage: this.variantImage(variant) || data.displayImage,
        };
        window.__dualProductSelection = detail;
        document.dispatchEvent(new CustomEvent('dual-product:variant-change', { detail }));
        document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));

        if (announce) {
          const announcer = this.querySelector('[data-dual-announcer]');
          if (announcer) announcer.textContent = `${detail.displayTitle}: ${variant.title}, ${this.formatMoney(variant.price)}`;
        }
      }

      updateModelCard(modelKey, variant) {
        const input = this.modelInputs.find((modelInput) => modelInput.dataset.modelKey === String(modelKey));
        const card = input?.nextElementSibling;
        if (!card) return;

        const price = card.querySelector('[data-model-price]');
        const compare = card.querySelector('[data-model-compare]');
        const saved = card.querySelector('[data-model-saved]');
        const hasSaving = Number(variant.compare_at_price || 0) > Number(variant.price || 0);

        if (price) price.textContent = this.formatMoney(variant.price);
        if (compare) {
          compare.textContent = hasSaving ? this.formatMoney(variant.compare_at_price) : '';
          compare.classList.toggle('hidden', !hasSaving);
        }
        if (saved) {
          saved.textContent = hasSaving
            ? `${this.dataset.saveLabel} ${this.formatMoney(variant.compare_at_price - variant.price)}`
            : '';
          saved.classList.toggle('hidden', !hasSaving);
        }
      }

      updateStock(data, variant) {
        const stockText = this.querySelector('[data-dual-stock-text]');
        const quantity = Number(data.inventory?.[variant.id] || 0);
        this.dataset.soldOut = String(!variant.available);
        if (!stockText) return;

        if (!variant.available) {
          stockText.textContent = this.dataset.soldOutLabel;
        } else if (quantity > 0) {
          stockText.textContent = `${this.dataset.inStockLabel} (${quantity})`;
        } else {
          stockText.textContent = this.dataset.inStockLabel;
        }
      }

      updateForms(variant) {
        const ids = [`product-form-${this.sectionId}`, `product-form-installment-${this.sectionId}`];
        ids.forEach((id) => {
          const input = document.querySelector(`#${CSS.escape(id)} [name="id"]`);
          if (!input) return;
          input.disabled = !variant.available;
          input.value = variant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        document.querySelectorAll('.sticky-atc-bar__form [name="id"]').forEach((input) => {
          if (input.tagName === 'SELECT' && !Array.from(input.options).some((option) => Number(option.value) === Number(variant.id))) {
            input.add(new Option(variant.title, variant.id, true, true));
          }
          input.disabled = !variant.available;
          input.value = variant.id;
        });

        document.querySelectorAll('.product__block--buy_buttons [name="add"]').forEach((button) => {
          button.disabled = !variant.available;
          const text = button.querySelector('.btn__text, span');
          if (text && window.FoxTheme?.variantStrings) {
            text.textContent = variant.available ? FoxTheme.variantStrings.addToCart : FoxTheme.variantStrings.soldOut;
          }
        });

        const productInfo = document.querySelector(`product-info[data-section="${CSS.escape(this.sectionId)}"]`);
        if (productInfo) {
          productInfo.currentVariant = variant;
          productInfo.dataset.productId = this.currentProduct.id;
        }
      }

      updateMainPrice(variant) {
        const root = document.getElementById(`price-${this.sectionId}`);
        const price = root?.querySelector('.f-price');
        if (!root || !price) return;

        const hasSaving = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
        price.classList.toggle('f-price--on-sale', hasSaving);
        price.classList.toggle('f-price--sold-out', !variant.available);

        const sale = price.querySelector('.f-price-item--sale');
        if (sale) sale.textContent = this.formatMoney(variant.price);
        price.querySelectorAll('.f-price-item--regular').forEach((regular) => {
          regular.innerHTML = hasSaving
            ? `<s>${this.formatMoney(variant.compare_at_price)}</s>`
            : this.formatMoney(variant.price);
        });

        let badge = root.querySelector('.f-badge--sale');
        if (hasSaving) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'btn btn--primary f-badge--sale';
            root.appendChild(badge);
          }
          badge.textContent = `${this.dataset.saveLabel} ${this.formatMoney(variant.compare_at_price - variant.price)}`;
          badge.hidden = false;
        } else if (badge) {
          badge.hidden = true;
        }

        this.updatePriceFromLabel(root, price);
      }

      updatePriceFromLabel(root, price) {
        const text = this.dataset.fromLabel;
        let label = root.querySelector('[data-dual-price-from]');

        if (!text) {
          label?.remove();
          return;
        }

        if (!label) {
          label = document.createElement('span');
          label.className = 'dual-product-price-from';
          label.dataset.dualPriceFrom = '';
        }

        label.textContent = text;
        const sale = price.querySelector('.f-price__sale');
        const regular = price.querySelector('.f-price__regular');
        const visiblePrice = price.classList.contains('f-price--on-sale') ? sale : regular;

        if (visiblePrice) {
          visiblePrice.insertAdjacentElement('beforebegin', label);
        } else if (!label.parentElement) {
          price.insertBefore(label, price.firstChild);
        }
      }

      updateProductCopy(data, userInitiated) {
        if (this.dataset.updateProductCopy !== 'true' || !userInitiated) return;
        const title = data.displayTitle || data.product.title;
        const titleBlock = document.querySelector('.product__block--title');
        titleBlock?.querySelectorAll('h1, h2').forEach((heading) => {
          heading.textContent = title;
        });

        if (titleBlock) {
          let modelCopy = titleBlock.querySelector('[data-dual-model-copy]');
          if (!modelCopy) {
            modelCopy = document.createElement('div');
            modelCopy.className = 'dual-product-copy';
            modelCopy.dataset.dualModelCopy = '';
            modelCopy.innerHTML = `
              <div class="dual-product-copy__eyebrow">
                <span data-dual-series-eyebrow></span>
                <span class="dual-product-copy__badge" data-dual-model-badge></span>
              </div>
            `;
            titleBlock.querySelector('h1')?.insertAdjacentElement('beforebegin', modelCopy);
          }

          let modelDetails = titleBlock.querySelector('[data-dual-model-details]');
          if (!modelDetails) {
            modelDetails = document.createElement('div');
            modelDetails.className = 'dual-product-copy__details';
            modelDetails.dataset.dualModelDetails = '';
            modelDetails.innerHTML = `
              <div class="dual-product-copy__type" data-dual-model-type></div>
              <div class="dual-product-copy__power" data-dual-model-power></div>
            `;
            titleBlock.querySelector('h1')?.insertAdjacentElement('afterend', modelDetails);
          }

          const eyebrow = modelCopy.querySelector('[data-dual-series-eyebrow]');
          const badge = modelCopy.querySelector('[data-dual-model-badge]');
          const type = modelDetails.querySelector('[data-dual-model-type]');
          const power = modelDetails.querySelector('[data-dual-model-power]');
          if (eyebrow) eyebrow.textContent = data.seriesEyebrow || '';
          if (badge) {
            badge.textContent = data.badge || '';
            badge.hidden = !data.badge;
          }
          if (type) type.textContent = data.modelType || '';
          if (power) power.textContent = data.modelPower || '';
        }

        const description = document.querySelector('.product__block--description');
        if (description && data.description) description.innerHTML = data.description;
      }

      updateInstallmentMessaging(variant) {
        document.querySelectorAll('[data-purchase-amount]').forEach((placement) => {
          placement.setAttribute('data-purchase-amount', String(variant.price));
        });
        if (window.Klarna?.OnsiteMessaging?.refresh) {
          window.Klarna.OnsiteMessaging.refresh();
        }
      }

      updateCoupon(data) {
        const productInfo = this.closest('product-info');
        const coupon = productInfo?.querySelector('.coupon-code') || document.querySelector('.coupon-code');
        if (!coupon) return;
        const heading = coupon.querySelector('.coupon-code__heading');
        const infoTitle = coupon.querySelector('.coupon-code__info-title');
        const copyButton = coupon.querySelector('copy-button[data-copy-text]');

        if (heading && data.couponHeading) heading.textContent = data.couponHeading;
        if (infoTitle && data.couponCode) infoTitle.textContent = data.couponCode;
        if (copyButton && data.couponCode) copyButton.dataset.copyText = data.couponCode;
      }

      async updateModelContent(data) {
        const container = document.querySelector('[data-dual-model-content]');
        if (!container) return;

        const pageUrl = data.contentPageUrl;
        const requestId = ++this.modelContentRequest;

        if (!pageUrl) {
          container.innerHTML = '';
          container.classList.add('is-empty');
          container.setAttribute('aria-busy', 'false');
          return;
        }

        container.classList.remove('is-empty');
        container.setAttribute('aria-busy', 'true');

        try {
          let contentHtml = this.modelContentCache.get(pageUrl);

          if (!contentHtml) {
            const response = await fetch(pageUrl, {
              credentials: 'same-origin',
              headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            if (!response.ok) {
              throw new Error(`Unable to load model content: ${response.status}`);
            }

            const pageHtml = await response.text();
            const pageDocument = new DOMParser().parseFromString(pageHtml, 'text/html');
            const source = pageDocument.querySelector('.dual-model-page-source');

            if (!source) {
              throw new Error('Model content wrapper was not found on the selected Page.');
            }

            contentHtml = source.innerHTML;
            this.modelContentCache.set(pageUrl, contentHtml);
          }

          // Ignore an older response if the customer already switched Model again.
          if (requestId !== this.modelContentRequest) return;

          container.innerHTML = contentHtml;

          // Page 通过 innerHTML 动态插入，浏览器不会自动执行其中的 script。
          await this.executePageScripts(container);

          container.setAttribute('aria-busy', 'false');

          // 兼容已经迁移到统一初始化器的新旧 Page 区块。
          if (typeof window.initDualPage === 'function') {
            await window.initDualPage(container);
          }

          document.dispatchEvent(
            new CustomEvent('dual-product:content-loaded', {
              detail: {
                sectionId: this.sectionId,
                modelKey: this.currentModelKey,
                pageUrl,
                container,
              },
            })
          );
        } catch (error) {
          console.error(error);

          if (requestId !== this.modelContentRequest) return;

          container.innerHTML = '';
          container.classList.add('is-empty');
          container.setAttribute('aria-busy', 'false');
        }
      }

      async executePageScripts(container) {
        const scripts = Array.from(container.querySelectorAll('script'));
        if (!scripts.length) return;

        window.__dualPageLoadedScripts = window.__dualPageLoadedScripts || new Map();

        for (const oldScript of scripts) {
          const type = (oldScript.getAttribute('type') || '').toLowerCase();

          // JSON 数据脚本只用于提供数据，不能当作 JavaScript 执行。
          if (type === 'application/json' || type === 'application/ld+json') continue;

          const src = oldScript.getAttribute('src');
          if (src) {
            const absoluteSrc = new URL(src, window.location.href).href;
            let loadPromise = window.__dualPageLoadedScripts.get(absoluteSrc);

            if (!loadPromise) {
              loadPromise = new Promise((resolve, reject) => {
                const existing = document.querySelector(`script[src="${CSS.escape(absoluteSrc)}"]`);
                if (existing) {
                  resolve();
                  return;
                }

                const script = document.createElement('script');
                script.src = absoluteSrc;
                script.async = false;
                script.addEventListener('load', resolve, { once: true });
                script.addEventListener('error', () => reject(new Error(`Failed to load Page script: ${absoluteSrc}`)), { once: true });
                document.head.appendChild(script);
              });
              window.__dualPageLoadedScripts.set(absoluteSrc, loadPromise);
            }

            try {
              await loadPromise;
            } catch (error) {
              window.__dualPageLoadedScripts.delete(absoluteSrc);
              throw error;
            }

            oldScript.remove();
            continue;
          }

          // 替换为新 script 节点，浏览器才会执行内联 JavaScript。
          const newScript = document.createElement('script');
          for (const attribute of oldScript.attributes) {
            newScript.setAttribute(attribute.name, attribute.value);
          }
          // Page 内容通常是在 DOMContentLoaded 之后插入。
          // 将旧区块监听的 DOMContentLoaded 改为本次动态加载专用事件。
          newScript.textContent = oldScript.textContent.replace(
            /document\.addEventListener\(\s*['"]DOMContentLoaded['"]\s*,/g,
            "document.addEventListener('dual-page:ready',"
          );
          oldScript.replaceWith(newScript);
        }

        // 供旧 Page 区块改造后监听；不会伪造 DOMContentLoaded。
        container.dispatchEvent(new CustomEvent('dual-page:ready', {
          bubbles: true,
          detail: { container },
        }));
        document.dispatchEvent(new CustomEvent('dual-page:ready', {
          detail: { container },
        }));
      }

      variantImage(variant) {
        return variant?.featured_image?.src || variant?.featured_image || null;
      }

      updateBrowserUrl(data, variant) {
        if (!data?.product?.url || !window.history?.replaceState) return;

        const nextUrl = new URL(data.product.url, window.location.origin);
        const currentUrl = new URL(window.location.href);

        // Keep preview links valid while testing an unpublished Shopify theme.
        ['preview_theme_id', 'preview_key'].forEach((key) => {
          if (currentUrl.searchParams.has(key)) {
            nextUrl.searchParams.set(key, currentUrl.searchParams.get(key));
          }
        });

        window.history.replaceState(
          { dualModelKey: this.currentModelKey },
          '',
          `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
        );
      }

      updateBreadcrumbs(data) {
        const title = data.displayTitle || data.product?.title;
        const productUrl = data.product?.url;
        if (!title) return;

        // This selector exists only on product pages that include the dual-product-selector.
        // Ordinary product pages never call this method.
        document.querySelectorAll('.section-breadcrumb nav.breadcrumbs').forEach((breadcrumb) => {
          const currentProduct = breadcrumb.querySelector('.breadcrumbs--last');
          if (!currentProduct) return;

          currentProduct.textContent = title;
          currentProduct.setAttribute('title', title);

          if (productUrl) {
            currentProduct.dataset.productUrl = productUrl;
          }
        });
      }

      updateGalleryImage(data, variant) {
        if (this.dataset.updateGalleryImage !== 'true') return;
        const productInfo = this.closest('product-info');
        const galleryWrappers = Array.from(productInfo?.querySelectorAll('[data-dual-product-gallery]') || []);

        if (galleryWrappers.length) {
          let activeGallery = null;
          galleryWrappers.forEach((wrapper) => {
            const isActive = wrapper.dataset.productId === String(data.product.id);
            wrapper.hidden = !isActive;
            if (isActive) activeGallery = wrapper.querySelector('media-gallery');
          });

          if (activeGallery) {
            requestAnimationFrame(() => {
              // 如果这套画廊之前因为处于 hidden 状态而被推迟初始化，
              // 这里在它真正显示出来、拿到正确宽度之后再补初始化，
              // 修复“点击缩略图后主图错乱”的问题。
              if (typeof activeGallery.ensureInit === 'function') {
                activeGallery.ensureInit();
              }
              activeGallery.sliderInstance?.slider?.update();
              activeGallery.thumbsInstance?.slider?.update();
              activeGallery.setActiveMedia?.(variant);
            });
          }
          return;
        }

        // Backward-compatible fallback for templates that render only one gallery.
        const imageUrl = this.variantImage(variant) || data.displayImage;
        if (!imageUrl) return;
        const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        const firstMedia = gallery?.querySelector('.product__media-item:not(.swiper-slide-duplicate)');
        const firstImage = firstMedia?.querySelector('img');
        if (firstImage) {
          firstImage.src = imageUrl;
          firstImage.srcset = imageUrl;
          firstMedia.dataset.src = imageUrl;
        }
        const firstThumb = gallery?.querySelector('.product__thumbs-item img');
        if (firstThumb) {
          firstThumb.src = imageUrl;
          firstThumb.srcset = imageUrl;
        }
      }
    }
  );
}